import { engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimatorExtended } from "./WinAnimatorExtended";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Symbol } from "../GameComponents/Symbol";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { Tweens } from "../Components/Effects/Tweens";
import Tween = gsap.Tween;
import { AppContext } from "../../Common/AppContext";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { Languages } from "../Utils/Languages";
import { PlayList } from "../Components/Sounds/PlayList";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { FreespinsView } from "../GameComponents/Freespins/FreespinsView";
import { TimelineElementExtended } from "./TimelineElementExtended";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorFreespins extends WinAnimatorExtended {
    //******************************************************************************************************************
    //  CONFIG
    //******************************************************************************************************************

    private static ExpandDurationFS: number = 165; //45f - ExpandAnimation 60f - SymbolTextShine text anim + 60f multiplier anim
    public static MultiplierDuration: number = 60; //60f winanimator.xml Multiplier animation

    private static readonly MultiplierWinAnimationIndex: number = 7;

    private hasExpandWild: boolean;
    public _multiplierAnimationDelay: FrameTimerEventEmitter;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._multiplierAnimationDelay = CreateFrameTimer("multiplierAnimationDelay");
        this._multiplierAnimationDelay.on(FrameTimerEvents.Complete, this.showWinWithMultiplierFinished.bind(this));
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected allowSymbolGoToCase(reelIndex: number, symbolIndex: number, caseType: string): boolean {
        if (this.currentSymbols[reelIndex][symbolIndex] != null) {
            return super.allowSymbolGoToCase(reelIndex, symbolIndex, caseType);
        }

        return true;
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        if (this.paylines != null) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            if (FreespinsView.FSPaylinesIndicator != null) {
                this.addChild(FreespinsView.FSPaylinesIndicator);
                FreespinsView.FSPaylinesIndicator.goToCase("HIDE");
            }

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    const symbol = this.currentSymbols[i][definition[i]];

                    if (symbol == null) {
                        this._expandWilds[i].goToCase("WIN");
                    } else {
                        if (caseType === Symbol.CaseCycle) {
                            symbol.goToCase(Symbol.CaseCycle + "_" + i);
                        } else {
                            symbol.goToCase(caseType);
                        }

                        if (
                            (caseType == Symbol.CaseWin || caseType == Symbol.CaseCycle) &&
                            this.currentWinSymbolsContainer != null
                        ) {
                            this.currentWinSymbolsContainer.addChild(symbol);
                        } else {
                            this.symbolsContainer.addChild(symbol);
                        }

                        symbols.push(symbol);
                    }
                }
            }
        }

        return symbols;
    }

    //******************************************************************************************************************
    //  EXPAND
    //******************************************************************************************************************

    public showExpandWilds(reelsData: Array<Communication.Game.IReel>, init: boolean = false): void {
        this.hasExpandWild = false;

        this.allSymbolsGoToCase(Symbol.CaseNormal, true);

        for (let i = 0; i < reelsData.length; i++) {
            if (reelsData[i].transform) {
                if (init) {
                    this._expandWilds[i].goToCase("INIT");
                } else {
                    this._expandWilds[i].goToCase("FS_START");
                    this.hasExpandWild = true;
                }

                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    const symbol = this.currentSymbols[i][j];

                    if (init) {
                        symbol.visible = false;
                    } else {
                        Tweens.removeTweens(symbol);
                        Tweens.customTween(symbol, {
                            tween: "alpha",
                            tweenValues: [1, 0],
                            tweenDuration: [WinAnimatorFreespins.ExpandDurationFS * 0.5],
                        });
                    }
                }

                this._expandWildsContainer.addChild(this._expandWilds[i]);
                this._expandWilds[i].position.y = 0;
            } else {
                this._expandWilds[i].goToCase("EMPTY");
            }
        }

        if (!init) {
            if (this.hasExpandWild) {
                PlayList.play("SND_FSExpandWild");
                this._expandWildsDelay.start(WinAnimatorFreespins.ExpandDurationFS);
            } else {
                this.expandWildFinished();
            }
        }

        const multiplier = this.getMultiplierCount();
        if (multiplier > 1) {
            FreespinsView.Multiplier.goToCase(multiplier + "_START");
        }
    }

    //******************************************************************************************************************
    //  MULTIPLIER
    //******************************************************************************************************************

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = 0;

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            if (this.hasExpandWild) {
                this.currentMultiplierIndex = WinAnimatorFreespins.MultiplierWinAnimationIndex;
            }

            if (!this.showCustomWin()) {
                this.showFirstWins();
            }

            this.moveToTop();
            return this.currentMultiplierIndex >= 3;
        } else {
            this.allSymbolsGoToCase(Symbol.CaseNormal, false);
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
            this.moveToTop();
            return false;
        }
    }

    protected createAnimations(): void {
        super.createAnimations();

        if (this.animations.length == 8) {
            const multiplierWinAnimation = this.animations[WinAnimatorFreespins.MultiplierWinAnimationIndex];
            multiplierWinAnimation.on("nomMultiplierWin", this.showWinWithMultiplier.bind(this));
        }
    }

    protected startAnimation(winValue?: number, fromWinValue?: number, startFrame?: number): void {
        this.currentAnimationIndex = this.getAnimationIndex();
        this.currentAnimationFinished = false;

        let currentWin = this.getCurrentWin();
        if (winValue != null) {
            currentWin = winValue;
        }

        let fromWin = 0;
        if (fromWinValue != null) {
            fromWin = fromWinValue;
        }

        const animation = <TimelineElementExtended>this.animations[this.currentAnimationIndex];
        animation.visible = true;
        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(
                parseInt(NumberUtils.devide(currentWin, BetManager.CoinSize).toString()),
                parseInt(NumberUtils.devide(fromWin, BetManager.CoinSize).toString()),
            );
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(currentWin, fromWin);
        }

        if (startFrame != null) {
            animation.skipToFrame(startFrame);
        }

        this.playGenieWinSound();
    }

    protected playGenieWinSound(): void {
        let hasGenieWin = false;
        for (let winIndex = 0; winIndex < this.currentWins.length; winIndex++) {
            const win = this.currentWins[winIndex];
            if (win.payoutId == 13) {
                hasGenieWin = true;
            }
        }

        if (hasGenieWin) {
            PlayList.play("SND_BaseBonusHaha");
        }
    }

    protected stopAnimation(): void {
        if (this.isMultiplierWinAnimation()) {
            this.handleMultiplierOutroAnim();
        }

        if (this.currentAnimationIndex != null) {
            const animation = this.animations[this.currentAnimationIndex];
            animation.visible = true;
            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                animation.goToOutro(parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()));
            } else {
                ImageTextCounterElement.Round = false;
                animation.goToOutro(this.currentWin);
            }

            this.currentAnimationIndex = null;
            this.currentAnimationFinished = true;
        }
    }

    protected isMultiplierWinAnimation(): boolean {
        return this.currentAnimationIndex == WinAnimatorFreespins.MultiplierWinAnimationIndex;
    }

    protected isHighWin(multiplierIndex: number): boolean {
        return multiplierIndex > 2;
    }

    protected handleMultiplierOutroAnim(): void {
        const finalMultiplierIndex = this.getCurrentMultiplierIndex();
        if (this.isHighWin(finalMultiplierIndex)) {
            const animation = this.animations[this.currentAnimationIndex];
            animation.stop();
            this.currentMultiplierIndex = finalMultiplierIndex;
            this.currentAnimationIndex = finalMultiplierIndex;
        }
    }

    protected showWinWithMultiplier(): void {
        const multiplier = this.getMultiplierCount();
        if (multiplier > 1) {
            FreespinsView.Multiplier.goToCase(multiplier + "_MULTIPLY");
            this.stopTimer();
            this._multiplierAnimationDelay.start(WinAnimatorFreespins.MultiplierDuration);
        }
    }

    protected showWinWithMultiplierFinished(): void {
        const finalMultiplierIndex = this.getCurrentMultiplierIndex();
        const animation = <TimelineElementExtended>this.animations[this.currentAnimationIndex];
        if (animation != null) {
            if (this.isHighWin(finalMultiplierIndex)) {
                //run high win animation
                if (FreespinsView.Transition != null) {
                    this.addChild(FreespinsView.Transition);
                    PlayList.play("SND_Multiplier");
                    FreespinsView.Transition.goToCase("FLASH");
                }
                PlayList.stop("SND_FSLoop");
                animation.stop();
                this.currentMultiplierIndex = finalMultiplierIndex;
                this.startAnimation(this.currentWin, this.getCurrentWin(), 30);
            } else {
                let winCounter = <ImageTextCounterElement>animation.getElementsContainer().childs["WinCounter"];
                if (winCounter != null) {
                    PlayList.play("SND_Multiplier");
                    winCounter.bitmapText.text = Languages.formatText(
                        Languages.FormatMoney,
                        this.currentWin.toString(),
                    );
                    winCounter.realign();
                }
            }
        }
    }

    protected getCurrentMultiplierIndex(): number {
        let finalMultiplierIndex = 0;
        for (let i = 0; i < this.multipliers.length; i++) {
            if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                finalMultiplierIndex = i;
            }
        }

        return finalMultiplierIndex;
    }

    protected getCurrentWin(): number {
        if (!this.hasExpandWild) {
            return this.currentWin;
        }

        const multiplier = this.getMultiplierCount();
        return parseFloat(NumberUtils.devide(this.currentWin, multiplier));
    }

    protected getMultiplierCount(): number {
        let multiplier = 1;

        if (this.currentReels != null) {
            for (let i = 0; i < this.reelCount; i++) {
                if (this.currentReels[i].transform != null) {
                    multiplier *= 2;
                }
            }
        }

        return multiplier;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorFreespins.ts
 **/